/* ============================================================================
   COLOR GALLERY + VISUAL COLOR COMPARE — Figma 877:185129.

   The gallery (D 877:185166 · M 877:185130) is a drawer of every colour's
   primary on-model thumbnail, the model lifted off the studio backdrop so
   she stands on the card's stone — ghosts are kept for Compare; a colour
   with no photography shows a PHOTO COMING SOON tile in its own hex —
   on the same shell and choreography as the size drawer:
   picking a card marks it, the drawer closes itself after a beat, and the
   page changes colour as it leaves. Desktop hover is the card's Figma Hover
   variant (1163:147389) — the thumbnail grows to the card's full width.

   Its Visual Color Compare card opens the takeover (D 1208:8610 · M 1209:8652):
   two colours' ghosts split by a draggable divider, a LEFT and a RIGHT rail of
   image chips in shade order (data.js SHADES). Usability follows Mammut's colour picker and the standard
   before/after slider: drag anywhere on the stage (not just the handle), click
   to jump, arrow keys on the handle, the hint retires after the first drag,
   and the colour already on the other side can't be picked twice.
   ========================================================================== */

import { animate, hover, stagger, MO, state, play, track, SPRING_RELEASE, $, $$, mqDesktop } from './mo.js';
import { createDrawer } from './drawers.js';
import { SHADES } from './data.js';
import { bindRoving, garmentHTML, swapText, announce } from './ui.js';

const AUTO_CLOSE_MS = 450; // the size drawer's beat
const canHover = matchMedia('(hover: hover) and (pointer: fine)');

/* ============================================================================
   COLOR GALLERY
   ========================================================================== */
/* placeholder copy flips to dark on the light swatches (yellow, heather…) */
const isLight = (hex) => {
  const m = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex || '');
  if (!m) return false;
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.55;
};
const cardMedia = (c) => {
  if (c.card) return `<img class="cg-card__img" src="${c.cutout || c.card}" alt="" loading="lazy" decoding="async" draggable="false">`;
  return `<span class="cg-card__placeholder${isLight(c.hex) ? ' is-light' : ''}" aria-hidden="true"><span>Photo coming soon</span></span>`;
};

const cardHTML = (c, selected) => `
  <button class="cg-card" type="button" aria-pressed="${c.slug === selected}" data-cg-color="${c.slug}"
    aria-label="${c.name}${c.surprise ? ', a surprise color' : c.card ? '' : ', photo coming soon'}" style="--swatch:${c.hex}">
    <span class="cg-card__head">
      <span class="cg-card__swatch${c.surprise ? ' cg-card__swatch--surprise' : ''}"></span>
      <span class="cg-card__label">${c.name}</span>
    </span>
    <span class="cg-card__media">${cardMedia(c)}</span>
  </button>`;

export function initColorGallery({ product, getColor, onSelect, onCompare }) {
  const root = $('[data-drawer="gallery"]');
  const drawer = createDrawer(root);
  const all = [...product.colors.core, ...product.colors.limited];
  $('[data-cg-count]', root).textContent = all.length;
  $('[data-cg-grid="core"]', root).innerHTML = product.colors.core.map((c) => cardHTML(c, getColor())).join('');
  $('[data-cg-grid="limited"]', root).innerHTML = product.colors.limited.map((c) => cardHTML(c, getColor())).join('');
  const cards = $$('.cg-card', root);
  const mark = (slug) => cards.forEach((card) => card.setAttribute('aria-pressed', String(card.dataset.cgColor === slug)));

  /* Hover variant: padding 8 → 12 and the thumbnail grows from the bottom to
     the card's inner width, passing under the label row. */
  cards.forEach((card) => {
    const media = $('.cg-card__media', card);
    hover(card, () => {
      if (!canHover.matches || state.reduced) return undefined;
      /* Figma's hover image fills the card inside its 12px padding; on the
         shorter limited cards the height is the limit, so its top stays under the label */
      const k = Math.min(1.3, (Math.min(card.clientWidth, card.clientHeight) - 24) / media.clientHeight);
      track(animate(media, { scale: k }, { duration: MO.slow, ease: MO.eEntrance }));
      return () => track(animate(media, { scale: 1 }, { duration: MO.base, ease: MO.eStandard }));
    });
  });

  let closeTimer = 0;
  root.addEventListener('click', (e) => {
    const card = e.target.closest('[data-cg-color]');
    if (!card) return;
    const slug = card.dataset.cgColor;
    mark(slug);
    play(card, { scale: [MO.scalePress, 1] }, SPRING_RELEASE);
    announce(`${all.find((c) => c.slug === slug).name} selected`);
    clearTimeout(closeTimer);
    /* the page changes colour as the drawer leaves, so the change is seen */
    closeTimer = setTimeout(() => { drawer.close(); onSelect(slug); }, state.reduced ? 200 : AUTO_CLOSE_MS);
  });
  root.addEventListener('drawer:close', () => clearTimeout(closeTimer));

  $('[data-open-compare]', root).addEventListener('click', () => {
    clearTimeout(closeTimer);
    drawer.close();
    onCompare();
  });

  return {
    open(trigger) {
      mark(getColor());
      $('.cg__body', root).scrollTop = 0;
      drawer.open(trigger);
      if (!state.reduced) {
        play([$('.cg__promo', root), ...cards], { opacity: [0, 1], y: [MO.riseXs, 0] },
          { duration: MO.base, ease: MO.eEntrance, delay: stagger(0.012, { startDelay: 0.16 }) });
      }
    },
    close: () => drawer.close(),
    get isOpen() { return drawer.isOpen; },
  };
}

/* ============================================================================
   VISUAL COLOR COMPARE
   ========================================================================== */
const RAIL_VISIBLE = 8; // Figma desktop rail: eight chips, then "+N"
const SHADE_RANK = new Map(SHADES.flat().map((slug, i) => [slug, i]));
const SHADE_FAMILY = new Map(SHADES.flatMap((family, k) => family.map((slug) => [slug, k])));

export function initCompare({ product, getColor }) {
  const root = $('[data-drawer="compare"]');
  const drawer = createDrawer(root, { mode: 'takeover' });
  const rank = (c) => SHADE_RANK.get(c.slug) ?? SHADE_RANK.size;
  const colors = [...product.colors.core, ...product.colors.limited].filter((c) => !c.surprise)
    .sort((a, b) => rank(a) - rank(b));
  const bySlug = (slug) => colors.find((c) => c.slug === slug);
  const stage = $('[data-vcc-stage]', root);
  const handle = $('[data-vcc-handle]', root);
  const hint = $('[data-vcc-hint]', root);
  const sheet = $('[data-vcc-sheet]', root);
  const grabber = $('[data-vcc-grabber]', root);
  const sides = ['left', 'right'];
  const el = Object.fromEntries(sides.map((s) => [s, {
    garment: $(`[data-vcc-garment="${s}"]`, root),
    chips: $(`[data-vcc-chips="${s}"]`, root),
    name: $(`[data-vcc-name="${s}"]`, root),
    tag: $(`[data-vcc-tag="${s}"]`, root),
    more: null,
  }]));
  const pick = { left: null, right: null };
  const win = { left: 0, right: 0 }; // the first of a collapsed rail's eight chips
  const tokens = { left: 0, right: 0 };
  const other = (side) => (side === 'left' ? 'right' : 'left');
  let split = 50;
  let splitAnim = null;
  let hintShown = false;

  /* ---- rails ---------------------------------------------------------- */
  const chipHTML = (c) => `
    <button class="vcc-chip" type="button" role="radio" aria-checked="false" tabindex="-1"
      aria-label="${c.name}${c.ghost ? '' : ', placeholder image'}" data-vcc-color="${c.slug}">
      ${garmentHTML(c, { chip: true })}
    </button>`;
  sides.forEach((side) => {
    const { chips } = el[side];
    const extra = colors.length - RAIL_VISIBLE;
    chips.innerHTML = colors.map(chipHTML).join('')
      + `<button class="vcc-chip vcc-chip--more" type="button" aria-expanded="false" data-vcc-more
          aria-label="Show all ${colors.length} colors">+${extra}</button>`;
    el[side].more = $('[data-vcc-more]', chips);
    el[side].more.addEventListener('click', () => expand(side, !chips.hasAttribute('data-expanded')));
    bindRoving(chips, (chip) => {
      if (chip.getAttribute('aria-disabled') === 'true') return;
      setColor(side, chip.dataset.vccColor);
    });
  });

  /* A collapsed rail shows eight chips around its pick, starting with the
     pick's shade family, so the colours it's most likely weighed against sit
     beside it (both rails open on their own family). It only moves when the
     pick is out of view or the rail is reset, never under the pointer. */
  function frame(side, { reset = false } = {}) {
    const i = colors.findIndex((c) => c.slug === pick[side]);
    if (i < 0) return;
    if (!reset && i >= win[side] && i < win[side] + RAIL_VISIBLE) return;
    const first = colors.findIndex((c) => SHADE_FAMILY.get(c.slug) === SHADE_FAMILY.get(pick[side]));
    win[side] = Math.max(0, Math.min(Math.max(first, i - RAIL_VISIBLE + 1), colors.length - RAIL_VISIBLE));
    $$('[role="radio"]', el[side].chips).forEach((chip, k) => {
      chip.toggleAttribute('data-overflow', k < win[side] || k >= win[side] + RAIL_VISIBLE);
    });
  }
  /* mobile shows every chip in a scrolling row: the pick's family starts it,
     with half of the colour before peeking in so the row reads as scrollable */
  function scrollToPick(side) {
    const { chips } = el[side];
    const start = $$('[role="radio"]', chips)[win[side]];
    if (!start || mqDesktop.matches) return;
    const peek = win[side] ? start.offsetWidth / 2 : 0;
    chips.scrollLeft += start.getBoundingClientRect().left - chips.getBoundingClientRect().left - 2 - peek;
  }

  function expand(side, on) {
    const { chips, more } = el[side];
    chips.toggleAttribute('data-expanded', on);
    more.setAttribute('aria-expanded', String(on));
    more.textContent = on ? 'Less' : `+${colors.length - RAIL_VISIBLE}`;
    more.setAttribute('aria-label', on ? 'Show fewer colors' : `Show all ${colors.length} colors`);
    if (!on) frame(side, { reset: true });
    if (on && !state.reduced) {
      play($$('.vcc-chip[data-overflow]', chips), { opacity: [0, 1], y: [MO.riseXs, 0] },
        { duration: MO.base, ease: MO.eEntrance, delay: stagger(0.012) });
    }
  }

  function syncRails() {
    sides.forEach((side) => {
      const taken = pick[other(side)];
      $$('[role="radio"]', el[side].chips).forEach((chip) => {
        const on = chip.dataset.vccColor === pick[side];
        chip.setAttribute('aria-checked', String(on));
        chip.tabIndex = on ? 0 : -1;
        const off = chip.dataset.vccColor === taken;
        if (off) chip.setAttribute('aria-disabled', 'true');
        else chip.removeAttribute('aria-disabled');
        chip.title = off ? `Showing on the ${other(side)}` : '';
      });
      frame(side); // the choice stays in view
    });
  }

  /* ---- colour ---------------------------------------------------------- */
  async function setColor(side, slug, { instant = false } = {}) {
    if (pick[side] === slug && !instant) return;
    const c = bySlug(slug);
    pick[side] = slug;
    syncRails();
    const label = c.name;
    if (instant) el[side].name.textContent = label;
    else swapText(el[side].name, label);
    el[side].tag.hidden = !!c.ghost;
    updateValueText();
    const box = el[side].garment;
    const token = ++tokens[side];
    if (!instant && box.firstElementChild) {
      await play(box, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished;
      if (token !== tokens[side]) return;
    }
    box.innerHTML = garmentHTML(c);
    await $('img', box)?.decode?.().catch(() => {});
    if (token !== tokens[side]) return;
    if (!instant) play(box, { opacity: [0, 1] }, { duration: MO.base, ease: MO.eEntrance });
    if (!instant) announce(`${side === 'left' ? 'Left' : 'Right'}: ${label}`);
  }

  /* ---- split ------------------------------------------------------------ */
  function updateValueText() {
    const l = Math.round(split);
    handle.setAttribute('aria-valuenow', String(l));
    if (pick.left && pick.right) {
      handle.setAttribute('aria-valuetext', `${bySlug(pick.left).name} ${l}%, ${bySlug(pick.right).name} ${100 - l}%`);
    }
  }
  function setSplit(v) {
    split = Math.max(0, Math.min(100, v));
    stage.style.setProperty('--split', `${split}%`);
    updateValueText();
  }
  function tweenSplit(to, duration = MO.quick) {
    splitAnim?.stop();
    if (state.reduced) { setSplit(to); return; }
    splitAnim = track(animate(split, to, { duration, ease: MO.eEntrance, onUpdate: setSplit }));
  }
  function showHint() {
    hintShown = true;
    play(hint, { opacity: [0, 1], y: [4, 0] }, { duration: MO.base, ease: MO.eEntrance, delay: 0.3 });
  }
  function retireHint() {
    if (!hintShown) return;
    hintShown = false;
    play(hint, { opacity: 0 }, { duration: MO.quick, ease: MO.eExit });
  }

  /* drag anywhere on the stage; a click jumps the divider there */
  let drag = null;
  const pct = (e) => { const r = stage.getBoundingClientRect(); return ((e.clientX - r.left) / r.width) * 100; };
  stage.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    drag = { id: e.pointerId, onHandle: !!e.target.closest('[data-vcc-handle]') };
    stage.setPointerCapture(e.pointerId);
    stage.classList.add('is-dragging');
    retireHint();
    splitAnim?.stop();
    if (drag.onHandle) play(handle, { scale: MO.scalePress }, { duration: MO.instant, ease: MO.eStandard });
    else tweenSplit(pct(e));
  });
  stage.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    splitAnim?.stop();
    setSplit(pct(e));
  });
  const endDrag = () => {
    if (!drag) return;
    if (drag.onHandle) play(handle, { scale: 1 }, state.reduced ? { duration: MO.instant } : SPRING_RELEASE);
    drag = null;
    stage.classList.remove('is-dragging');
  };
  stage.addEventListener('pointerup', endDrag);
  stage.addEventListener('pointercancel', endDrag);

  handle.addEventListener('keydown', (e) => {
    const step = e.shiftKey ? 10 : 2;
    const keys = { ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step, PageDown: -10, PageUp: 10 };
    let next = null;
    if (e.key in keys) next = split + keys[e.key];
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = 100;
    if (next === null) return;
    e.preventDefault();
    retireHint();
    splitAnim?.stop();
    setSplit(next);
  });

  /* mobile colour sheet: the grabber folds it away to see the whole stage */
  grabber.addEventListener('click', () => {
    const folded = sheet.toggleAttribute('data-collapsed');
    grabber.setAttribute('aria-expanded', String(!folded));
    grabber.setAttribute('aria-label', folded ? 'Show colors' : 'Hide colors');
  });

  return {
    open(trigger) {
      const current = getColor();
      const left = bySlug(current) ? current : 'navy';
      const right = left === 'burgundy' ? 'navy' : 'burgundy'; // Figma's pair: Navy / Burgundy
      sides.forEach((s) => expand(s, false));
      sheet.removeAttribute('data-collapsed');
      grabber.setAttribute('aria-expanded', 'true');
      pick.left = null; pick.right = null;
      setColor('left', left, { instant: true });
      setColor('right', right, { instant: true });
      sides.forEach((s) => frame(s, { reset: true }));
      setSplit(50);
      drawer.open(trigger);
      sides.forEach(scrollToPick);
      if (mqDesktop.matches) showHint();
      /* one slow sweep says "this moves" before anyone has to guess */
      if (!state.reduced) {
        splitAnim = track(animate(50, [50, 60, 50], {
          duration: MO.cinematic, delay: 0.5, ease: MO.eEmphasis, onUpdate: setSplit,
        }));
      }
    },
    close: () => drawer.close(),
    get isOpen() { return drawer.isOpen; },
  };
}
